import { Fragment } from "react";
import { MoveRight, Quote, ExternalLink } from "lucide-react";

/**
 * Bloques de contenido enriquecido para artículos del blog en formato "guía".
 * Se usan vía el campo opcional `blocks` en blogPosts.js (los artículos existentes
 * con solo `content` en prosa no se ven afectados). Todos los bloques usan HTML
 * semántico (h3, p, ol/li, blockquote) y estilos alineados 100% a la paleta del sitio.
 */

const renderBolded = (text) =>
  String(text)
    .split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g)
    .map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={i} className="font-semibold text-navy-800">
            {part.slice(2, -2)}
          </strong>
        );
      }
      const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        return (
          <a
            key={i}
            href={linkMatch[2]}
            {...(linkMatch[2].startsWith("/") ? {} : { target: "_blank", rel: "noopener noreferrer" })}
            data-testid="blog-block-inline-link"
            className="text-[#F26522] font-semibold underline underline-offset-2 decoration-[#F26522]/50 hover:decoration-[#F26522]"
          >
            {linkMatch[1]}
          </a>
        );
      }
      return part;
    });

const renderBody = (text, pClass = "text-slate-600 text-base sm:text-lg leading-relaxed") =>
  String(text)
    .split(/\n\n/)
    .map((p, i) => (
      <p key={i} className={`${pClass} ${i > 0 ? "mt-3" : ""}`}>
        {renderBolded(p)}
      </p>
    ));

function StepProgress({ total }) {
  return (
    <div className="reveal mb-12 lg:mb-14" data-testid="blog-step-progress">
      <div className="flex items-center gap-2 sm:gap-3">
        {Array.from({ length: total }, (_, i) => (
          <Fragment key={i}>
            <span className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#0A1128] text-white font-display font-bold text-xs sm:text-sm flex items-center justify-center shrink-0">
              {i + 1}
            </span>
            {i < total - 1 && <div className="flex-1 h-px bg-slate-200" />}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function GuideStep({ step, testId }) {
  return (
    <article data-testid={testId} className="reveal">
      <span className="inline-block text-[11px] font-bold tracking-[0.2em] uppercase bg-[#F26522] text-white px-3 py-1 rounded-full">
        Paso {String(step.number).padStart(2, "0")} · {step.name}
      </span>
      <h3 className="mt-4 font-display font-extrabold text-2xl sm:text-3xl text-navy-800 leading-tight">
        {step.title}
      </h3>
      <div className="mt-4 space-y-4">{renderBody(step.body)}</div>
    </article>
  );
}

function StatCallout({ stat, testId }) {
  return (
    <div
      data-testid={testId}
      className="reveal my-5 rounded-2xl border border-[#F26522]/25 bg-[#FEF3EB]/50 px-6 py-8 sm:px-10 text-center"
    >
      <p className="font-display font-extrabold text-[#F26522] text-5xl sm:text-6xl tracking-tight leading-none">
        {stat.value}
      </p>
      <p className="mt-3 font-display font-bold text-navy-800 text-lg sm:text-xl">{stat.label}</p>
      {stat.subtext && <p className="mt-1 text-slate-500 text-sm sm:text-base">{stat.subtext}</p>}
    </div>
  );
}

function ChipRow({ chips, testId }) {
  return (
    <div data-testid={testId} className="reveal my-5 flex flex-wrap gap-2.5 sm:gap-3">
      {chips.items.map((item, i) => (
        <span
          key={i}
          className="inline-flex items-center border border-[#F26522]/30 bg-white text-navy-800 font-semibold text-xs sm:text-sm tracking-wide uppercase px-3.5 py-2 rounded-full"
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function QuoteCallout({ quote, testId }) {
  return (
    <blockquote data-testid={testId} className="reveal my-5 flex gap-4">
      <Quote className="w-8 h-8 sm:w-10 sm:h-10 text-[#F26522] shrink-0" fill="currentColor" />
      <p className="font-display font-extrabold text-navy-800 text-2xl sm:text-3xl leading-snug italic">
        {quote.text}
      </p>
    </blockquote>
  );
}

function CascadeDiagram({ cascade, testId }) {
  return (
    <ol data-testid={testId} className="reveal my-5 flex flex-col items-stretch sm:flex-row sm:items-center gap-3 sm:gap-4">
      {cascade.levels.map((level, i) => (
        <Fragment key={i}>
          <li className="flex-1 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-5 text-center">
            <p className="font-display font-extrabold text-navy-800 text-sm sm:text-base tracking-wide uppercase">
              {level.label}
            </p>
            <p className="mt-1.5 text-slate-500 text-xs sm:text-sm leading-relaxed">{level.desc}</p>
          </li>
          {i < cascade.levels.length - 1 && (
            <li aria-hidden="true" className="flex items-center justify-center rotate-90 sm:rotate-0">
              <MoveRight className="w-5 h-5 text-[#F26522]" />
            </li>
          )}
        </Fragment>
      ))}
    </ol>
  );
}

function PunchlineBanner({ punchline, testId }) {
  return (
    <div
      data-testid={testId}
      className="reveal mt-6 rounded-2xl bg-[#0A1128] px-6 py-10 sm:px-10 text-center"
    >
      {punchline.lines.map((line, i) => (
        <p
          key={i}
          className={`font-display font-extrabold leading-tight ${
            i === 0 ? "text-white text-xl sm:text-2xl" : "text-[#F26522] text-2xl sm:text-3xl"
          } ${i > 0 ? "mt-2" : ""}`}
        >
          {line}
        </p>
      ))}
    </div>
  );
}

function SectionHeading({ heading, testId }) {
  return (
    <div data-testid={testId} className="reveal mt-4 flex items-center gap-4">
      {heading.badge && (
        <span className="w-11 h-11 rounded-full bg-[#F26522] text-white font-display font-extrabold text-xl flex items-center justify-center shrink-0">
          {heading.badge}
        </span>
      )}
      <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-navy-800 leading-tight">{heading.text}</h2>
    </div>
  );
}

function FaqList({ faq, testId }) {
  return (
    <div data-testid={testId} className="reveal space-y-4">
      {faq.items.map((item, i) => (
        <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-5 sm:px-6">
          <h3 className="font-display font-bold text-navy-800 text-base sm:text-lg leading-snug">{item.q}</h3>
          <div className="mt-2">{renderBody(item.a, "text-slate-600 text-base leading-relaxed")}</div>
        </div>
      ))}
    </div>
  );
}

function Callout({ callout, testId }) {
  return (
    <div data-testid={testId} className="reveal mt-6 rounded-2xl border-l-4 border-[#F26522] bg-[#FEF3EB]/60 px-6 py-7 sm:px-8">
      {renderBody(callout.text, "text-navy-800 text-base sm:text-lg leading-relaxed")}
    </div>
  );
}

function InlineImage({ image, testId }) {
  return (
    <figure data-testid={testId} className="reveal my-6 max-w-[560px]">
      <img
        src={image.src}
        alt={image.alt}
        loading="lazy"
        className="w-full h-auto rounded-2xl border border-slate-200 shadow-sm"
      />
    </figure>
  );
}

function FurtherReading({ links, testId }) {
  return (
    <div data-testid={testId} className="reveal mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8">
      <span className="inline-block text-[11px] font-bold tracking-[0.2em] uppercase text-[#F26522]">
        {links.heading || "Para profundizar"}
      </span>
      <ul className="mt-4 space-y-3">
        {links.items.map((item, i) => {
          const internal = item.url.startsWith("/");
          return (
            <li key={i}>
              <a
                href={item.url}
                {...(internal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                data-testid={`blog-further-reading-link-${i}`}
                className="group inline-flex items-start gap-2 text-navy-800 font-semibold text-sm sm:text-base hover:text-[#F26522] transition-colors"
              >
                <span className="underline decoration-slate-300 group-hover:decoration-[#F26522]">{item.label}</span>
                {internal ? (
                  <MoveRight className="w-3.5 h-3.5 shrink-0 mt-1 text-slate-400 group-hover:text-[#F26522]" />
                ) : (
                  <ExternalLink className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-400 group-hover:text-[#F26522]" />
                )}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Renderizador del campo opcional `blocks` de un post. El post actual solo usa
 * `content` cuando no hay `blocks`, por lo que los artículos existentes no cambian.
 */
export function BlogBlocks({ blocks }) {
  const steps = blocks.filter((b) => b.type === "step");
  const totalSteps = steps.length;

  return (
    <div data-testid="blog-guide-content">
      {totalSteps > 1 && <StepProgress total={totalSteps} />}
      <div className="space-y-7 sm:space-y-9">
        {blocks.map((block, i) => {
          const key = `${block.type}-${i}`;
          switch (block.type) {
            case "paragraph":
              return (
                <div key={key} data-testid={`blog-block-${block.type}-${i}`} className="reveal">
                  {renderBody(block.text)}
                </div>
              );
            case "step":
              return <GuideStep key={key} step={block} testId={`blog-block-step-${block.number}`} />;
            case "stat":
              return <StatCallout key={key} stat={block} testId={`blog-block-stat-${i}`} />;
            case "chips":
              return <ChipRow key={key} chips={block} testId={`blog-block-chips-${i}`} />;
            case "quote":
              return <QuoteCallout key={key} quote={block} testId={`blog-block-quote-${i}`} />;
            case "cascade":
              return <CascadeDiagram key={key} cascade={block} testId={`blog-block-cascade-${i}`} />;
            case "punchline":
              return <PunchlineBanner key={key} punchline={block} testId={`blog-block-punchline-${i}`} />;
            case "heading":
              return <SectionHeading key={key} heading={block} testId={`blog-block-heading-${i}`} />;
            case "faq":
              return <FaqList key={key} faq={block} testId={`blog-block-faq-${i}`} />;
            case "callout":
              return <Callout key={key} callout={block} testId={`blog-block-callout-${i}`} />;
            case "image":
              return <InlineImage key={key} image={block} testId={`blog-block-image-${i}`} />;
            case "links":
              return <FurtherReading key={key} links={block} testId={`blog-block-links-${i}`} />;
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
}
